// Клиент запросов и его хранилище между сессиями.
//
// Правило 11: через 0 мс человек видит каркас и данные ПРОШЛОЙ сессии с пометкой времени,
// через 300 мс их подменяют свежие. Поэтому кэш переживает перезагрузку страницы: он
// сбрасывается в localStorage и поднимается оттуда мгновенно, до первого ответа сервера.

import { MutationCache, QueryClient } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { removeOldestQuery } from '@tanstack/react-query-persist-client';

import { изМеты, подтвердить } from '@/shared/ui/toast/подтверждение';

/**
 * Версия формы кэша. Меняется при несовместимой правке формы данных в кэше — старая
 * запись тогда выбрасывается целиком, а не читается в неверные типы.
 */
export const CACHE_BUSTER = 'bark-1';

/** Сутки. Ночь между рабочими днями кэш переживает, недельный труп — нет. */
export const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export const queryClient = new QueryClient({
  // Подтверждение удачного действия — ЗДЕСЬ, один раз на весь кабинет.
  //
  // До 04.09.2026 его поднимали руками, и подняли ровно в одном месте из ста двадцати трёх:
  // выгрузка в Excel. Человек закрывал ведомость, панель исчезала — и узнать, прошло ли, было
  // негде. Вписывать вызов в каждый экран значит забыть его в половине и поссорить
  // формулировки, поэтому действие ОБЪЯВЛЯЕТ свою фразу одной строкой `meta`, а показывает её
  // общий обработчик удачи.
  //
  // Нет объявления — нет подтверждения, и это решение экрана, а не забывчивость: переключателю
  // в настройках оно не нужно, он сам себе подтверждение.
  mutationCache: new MutationCache({
    onSuccess: (данные, ввод, _context, mutation) => {
      const весть = изМеты(mutation.options.meta, ввод, данные);
      if (весть) подтвердить(весть);
    },
  }),
  defaultOptions: {
    queries: {
      // Сборщик держит запись не меньше срока хранения на диске: иначе запись умирает в
      // памяти раньше, чем её успели бы показать из хранилища.
      gcTime: CACHE_MAX_AGE_MS,
      // Полминуты данные считаются свежими: переключение представлений туда-обратно не
      // порождает лавину повторных запросов.
      staleTime: 30_000,
      retry: 1,
    },
  },
});

/**
 * Поднятое из хранилища — данные ПРОШЛОЙ сессии, и свежими они не считаются ни минуты.
 *
 * Правило 11: через 0 мс человек видит прошлое, через 300 мс его подменяет свежее. Но полминуты
 * свежести (`staleTime`) отсчитываются от получения данных, а не от их показа: перезагрузка в эти
 * полминуты поднимала ведомость из хранилища как свежую, и сервер не спрашивался вовсе. Отметка
 * «устарело», поставленная действием, до хранилища при этом могла и не доехать: оно пишется не
 * чаще раза в секунду. Замер 20.10.2026: «Пересчитать весь период», перезагрузка следом — и
 * ведомость показала номера правок до пересчёта, а разовое удержание ответило «успели изменить»,
 * хотя ведомость никто не трогал; официальный период тем же путём открылся прошлым.
 *
 * Помечается устаревшим всё поднятое, без запроса: запрос пошлёт экран, когда покажет данные.
 */
export function поднятоеНесвежее(client: QueryClient): Promise<void> {
  return client.invalidateQueries({ refetchType: 'none' });
}

export const persister = createSyncStoragePersister({
  key: 'bark.query-cache',
  // Весь кэш лежит в ОДНОМ ключе хранилища, и место под него ограничено — на планшете жёстче,
  // чем в браузере. Без этой строки переполнение проглатывается молча: обещание правила 11
  // («0 мс — данные прошлой сессии») перестаёт выполняться, и никто об этом не узнает.
  // Так самая старая выборка вытесняется, а свежие переживают перезапуск.
  retry: removeOldestQuery,
  // В тестовой среде окна может не быть — тогда кэш просто не сохраняется между сессиями.
  storage: typeof window === 'undefined' ? undefined : window.localStorage,
});
